import { withStyles } from '@material-ui/styles';
import React, { PureComponent } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import { translate } from '../../../base/i18n';
import { connect } from '../../../base/redux';
import { hideNotification } from '../../actions';
import { areThereNotifications } from '../../functions';

import Notification from './Notification';

type Props = {

    /**
     * Whether we are a SIP gateway or not.
     */
    _iAmSipGateway: boolean,

    /**
     * Whether or not the chat is open.
     */
    _isChatOpen: boolean,

    /**
     * The notifications to be displayed, with the first index being the
     * notification at the top and the rest shown below it in order.
     */
    _notifications: Array<Object>,

    /**
     * JSS classes object.
     */
    classes: Object,

    /**
     * Invoked to update the redux store in order to remove notifications.
     */
    dispatch: Function,

    /**
     * Whether or not the notifications are displayed in a portal.
     */
    portal?: boolean,

    /**
     * Invoked to obtain translated strings.
     */
    t: Function,
};

const useStyles = () => {
    return {
        container: {
            position: 'absolute',
            left: '16px',
            bottom: '90px',

            // Shrink to the content; the old fixed widths are now the cap.
            width: 'fit-content',
            minWidth: '400px',
            maxWidth: 'min(450px, calc(100% - 32px))',
            zIndex: 600,

            '@media (min-width: 1440px)': {
                maxWidth: 'min(480px, calc(100% - 32px))'
            },

            '@media (min-width: 1920px)': {
                maxWidth: 'min(500px, calc(100% - 32px))'
            },

            '@media (max-height: 800px)': {
                bottom: '75px'
            },

            '@media (max-height: 600px)': {
                bottom: '65px'
            },

            '@media (max-width: 500px)': {
                left: '16px',

                // 400px doesn't fit a phone; span the screen minus the gutters.
                minWidth: 'calc(100% - 32px)',
                maxWidth: 'calc(100% - 32px)'
            }
        },

        containerPortal: {
            maxWidth: 'calc(100% - 32px)'
        },

        containerChatOpen: {
            left: '331px',

            '@media (max-width: 768px)': {
                left: '16px'
            }
        },

        transitionGroup: {
            '& > *': {
                marginBottom: '12px',
                borderRadius: '3px!important',
                position: 'relative',

                '@media (max-height: 800px)': {
                    marginBottom: '8px',
                    borderRadius: '3px!important'
                }
            }
        }
    };
};

/**
 * CSSTransition wrapper owning its node ref. TransitionGroup injects its
 * transition props (in, onExited, ...) which are forwarded as-is.
 *
 * @param {Object} props - The props injected by TransitionGroup.
 * @returns {ReactElement}
 */
function NotificationTransition({ children, ...props }) {
    const nodeRef = React.useRef(null);

    return (
        <CSSTransition
            { ...props }
            appear = { true }
            classNames = 'notification'
            nodeRef = { nodeRef }
            timeout = { 250 }>
            <div ref = { nodeRef }>
                {children}
            </div>
        </CSSTransition>
    );
}

/**
 * Implements a React {@link Component} which displays notifications and handles
 * automatic dismissal after a notification is shown for a defined timeout
 * period.
 *
 * @augments {Component}
 */
// eslint-disable-next-line react/no-multi-comp
class NotificationsContainer extends PureComponent<Props> {
    _timeouts: Map<string, TimeoutID>;

    /**
     * Initializes a new {@code NotificationsContainer} instance.
     *
     * @inheritdoc
     */
    constructor(props: Props) {
        super(props);

        this._timeouts = new Map();

        // Bind event handlers so they are only bound once for every instance.
        this._onDismissed = this._onDismissed.bind(this);

    }

    /**
     * Sets a timeout for each notification, where applicable.
     *
     * @inheritdoc
     */
    componentDidMount() {
        this._updateTimeouts();
    }

    /**
     * Sets a timeout for each notification, where applicable.
     *
     * @inheritdoc
     */
    componentDidUpdate() {
        this._updateTimeouts();
    }

    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     * @returns {ReactElement}
     */
    render() {
        if (this.props._iAmSipGateway) {
            return null;
        }

        return (
            <div
                className = { `${this.props.classes.container} ${this.props.brandedConference ? 'branded' : 'noBranded'}  ${this.props.portal
                    ? this.props.classes.containerPortal
                    : ''
                }` }
                id = 'notifications-container'>
                <TransitionGroup
                    className = { this.props.classes.transitionGroup }>
                    {this._renderFlags()}
                </TransitionGroup>
            </div>
        );
    }

    _onDismissed: (number) => void;

    /**
     * Emits an action to remove the notification from the redux store so it
     * stops displaying.
     *
     * @param {number} uid - The id of the notification to be removed.
     * @private
     * @returns {void}
     */
    _onDismissed(uid) {
        const timeout = this._timeouts.get(uid);

        if (timeout) {
            clearTimeout(timeout);
            this._timeouts.delete(uid);
        }

        this.props.dispatch(hideNotification(uid));
    }

    /**
     * Renders notifications to display as ReactElements. An empty array will
     * be returned if notifications are disabled.
     *
     * @private
     * @returns {ReactElement[]}
     */
    _renderFlags() {
        const { _notifications } = this.props;

        return _notifications.map(notification => {
            const { props, uid } = notification;

            // The id attribute is necessary as {@code FlagGroup} looks for
            // either id or key to set a key on notifications, but accessing
            // props.key will cause React to print an error.
            return (
                <NotificationTransition key = { uid }>
                    <Notification
                        { ...props }
                        id = { uid }
                        onDismissed = { this._onDismissed }
                        uid = { uid } />
                </NotificationTransition>
            );
        });
    }

    /**
     * Updates the timeouts for every notification.
     *
     * @returns {void}
     */
    _updateTimeouts() {
        const { _notifications } = this.props;

        for (const notification of _notifications) {
            if (
                notification.timeout
                && notification.props.isDismissAllowed !== false
                && !this._timeouts.has(notification.uid)
            ) {
                const { timeout, uid } = notification;
                const timerID = setTimeout(() => {
                    this._onDismissed(uid);
                }, timeout);

                this._timeouts.set(uid, timerID);
            }
        }
    }
}

/**
 * Maps (parts of) the Redux state to the associated props for this component.
 *
 * @param {Object} state - The Redux state.
 * @private
 * @returns {Props}
 */
function _mapStateToProps(state) {
    const { notifications } = state['features/notifications'];
    const { iAmSipGateway } = state['features/base/config'];
    const { isOpen: isChatOpen } = state['features/chat'];
    const _visible = areThereNotifications(state);
    const brandedConference = state['features/base/config'].interfaceConfig.applyMeetingSettings;

    return {
        _iAmSipGateway: Boolean(iAmSipGateway),
        _isChatOpen: isChatOpen,
        brandedConference,
        _notifications: _visible ? notifications : []
    };
}

export default translate(
    connect(_mapStateToProps)(withStyles(useStyles)(NotificationsContainer))
);
